import React, { FC } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Button, Tag } from "antd";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { inject, observer } from "mobx-react";
dayjs.extend(advancedFormat);

interface Props {
  events: any[];
  controller?: any;
}

const Events: FC<Props> = inject("controller")(
  observer((props) => {
    const navigate = useNavigate();

    const handleRegister = (event: any) => {
      props.controller.selectedEvent = event;
      navigate(`/register/${event._id}`);
    };

    const handleLateEntryClick = (event: any) => {
      props.controller.selectedEvent = event;
      props.controller.showLateEntryModal = true;
    };

    const handleApplicationRedirect = (url: string) => {
      window.open(url, "_blank");
    };

    const handleInfoClick = (event: any) => {
      props.controller.selectedEvent = event;
      props.controller.showInfoModal = true;
    };

    const renderLateEnries = (event) => {
      const entriesLeft = event.entriesLeft;

      if (!event.active || !event.lateEntries) {
        return null;
      }

      if (entriesLeft > 0 || entriesLeft === null) {
        return null;
      }

      return (
        <Button onClick={() => handleLateEntryClick(event)}>
          Late Entry
        </Button>
      );
    };

    const renderRegisterButton = (event) => {
      const entriesLeft = event.entriesLeft;

      let entriesActive = event.active;
      let buttonText = entriesActive ? "Apply" : "Applications Out Soon";

      //check if event date expired
      if (dayjs(event.eventDate).isBefore(dayjs())) {
        entriesActive = false;
        buttonText = "Applications Closed";
      }

      //event not hosted on application
      if (event.applicationUrl) {
        return (
          <Button
            type="primary"
            block
            onClick={() => handleApplicationRedirect(event.applicationUrl)}
            disabled={!entriesActive}
          >
            {buttonText}
          </Button>
        );
      }

      //check if limit has been reached
      if (entriesLeft !== null && entriesLeft <= 0) {
        entriesActive = false;
        buttonText = "Sold Out";
      }

      return (
        <Button type="primary" block onClick={() => handleRegister(event)} disabled={!entriesActive}>
          {buttonText}
        </Button>
      );
    };

    const renderRemainingEntries = (event) => {
      const entriesLeft = event.entriesLeft;

      if (!event.active || !entriesLeft || entriesLeft > 50 || entriesLeft < 0) {
        return null;
      }

      return (
        <Tag color="red" className="mr-0 font-semibold shrink-0">
          {entriesLeft + " Entries Left"}
        </Tag>
      );
    };

    const renderResultsLink = (event) => {
      if (!event.resultsUrl) {
        return null;
      }

      return <Button onClick={() => handleApplicationRedirect(event.resultsUrl)}>Results</Button>;
    };

    const renderPhotosLink = (event) => {
      if (!event.photosUrl) {
        return null;
      }

      return <Button onClick={() => handleApplicationRedirect(event.photosUrl)}>Photos</Button>;
    };

    const renderInfoButton = (event) => {
      if (!event.info) {
        return null;
      }

      return <Button onClick={() => handleInfoClick(event)}>Event Info</Button>;
    };

    const renderEvents = () => {
      return props.events.map((event) => {
        return (
          <div
            key={event._id}
            className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col"
          >
            <img src={event.image} alt={event.name} loading="lazy" className="w-full h-auto" />
            <div className="p-4 flex flex-col flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="font-bold text-lg leading-snug">{event.name}</div>
                {renderRemainingEntries(event)}
              </div>
              <p className="mt-1 mb-4 text-neutral-500">
                {event.isBundle ? "Discounted Price" : dayjs(event.eventDate).format("Do MMMM YYYY - HH:mm")}
              </p>
              <div className="mt-auto flex flex-col gap-2">
                {renderRegisterButton(event)}
                <div className="flex flex-wrap gap-2">
                  {renderLateEnries(event)}
                  {renderResultsLink(event)}
                  {renderPhotosLink(event)}
                  {renderInfoButton(event)}
                </div>
              </div>
            </div>
          </div>
        );
      });
    };

    return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{renderEvents()}</div>;
  })
);

export default Events;
